// Hideo - script editor's writing surface: the selection menu (#selMenu), which appears when text is selected, on phones
// too, and the real colours of the selection under the toolbar's "A" and highlighter. Ported from the legacy
// editor/editor.js with the same HTML (the menu is drawn as a string), except that the buttons carry their command
// (data-act, data-arg: the swatch's colour) instead of inline handlers: installSelMenu adds the menu's own listeners.
// Buttons act on "pointerdown" with preventDefault, so tapping them keeps the selection. The selection at the moment the
// menu appears is kept in selRange (on phones a tap can move it before the command).
// Colour swatches: every tap applies the colour even if it's the same as before (with the browser's colour picker,
// picking the same colour again did nothing).
import { t } from '../../../i18n'
import { esc } from '../../../utils/html'
import { useToolbarStore } from '../../../stores/toolbar'
import { useEditorStore } from '../../../stores/editor'
import { useDialogsStore } from '../../../stores/dialogs'
import { useCommentsStore } from '../../../stores/comments'
import { rgbHex } from '../colors'
import { applyBlock, applyColor, clearFormat, clearTextColor, insertLink } from './commands'
import { byId, edBody, elOf, ex, pageRange, savedRange, setSavedRange } from './core'

const SEL_COLORS = ['#e85d4e', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#a855f7', '#ec4899', '#9ca3af']
const SEL_HL = ['#fde047', '#86efac', '#93c5fd', '#f9a8d4', '#fdba74']
/** A touch screen (read once, at install) */
let selTouch = false
let selRange: Range | null = null, selPanel = '', selTimer: ReturnType<typeof setTimeout> | undefined, selPicking = false
/** Current colour of the selected text (or under the cursor): the bar under the "A" and under the highlighter shows the
 *  text's REAL colour, not the last one used. Updated whenever the selection moves. */
let selCur = { fc: '', bg: '' }

const selBtn = (act: string, inner: string, title: string) => `<button type="button" data-act="${act}" title="${title}" class="tb relative">${inner}</button>`
const selSwatch = (act: string, c: string, title: string) => `<button type="button" data-act="${act}" data-arg="${esc(c)}" title="${title}" class="w-7 h-7 rounded-full border border-gray-600 flex-shrink-0 flex items-center justify-center" style="background:${c}"></button>`
function renderSelMenu(): void {
  const EDTB = useToolbarStore().tb
  const sep = '<span class="w-px h-5 bg-gray-800 mx-0.5 flex-shrink-0"></span>', on = (k: string) => selPanel === k ? 'text-brand' : ''
  const custom = (act: string, val: string) => `<label data-picker class="tb flex items-center justify-center cursor-pointer relative" title="${t('Other colour')}"><i class="fas fa-eyedropper"></i><input type="color" value="${val}" class="absolute inset-0 opacity-0 w-full h-full cursor-pointer" data-act="${act}"></label>`
  const none = (act: string, title: string) => `<button type="button" data-act="${act}" title="${title}" class="w-7 h-7 rounded-full border border-gray-600 flex-shrink-0 flex items-center justify-center text-gray-400 bg-darkinput"><i class="fas fa-ban text-[11px]"></i></button>`
  byId('selMenu').innerHTML = `<div class="flex flex-wrap items-center gap-0.5 ${selTouch ? 'max-w-[19rem]' : ''}">
        ${selBtn('p', '<span class="text-xs font-bold">T</span>', t('Normal text'))}${selBtn('h1', '<span class="text-xs font-black">H1</span>', t('Heading 1'))}${selBtn('h2', '<span class="text-xs font-black">H2</span>', t('Heading 2'))}${selBtn('h3', '<span class="text-xs font-black">H3</span>', t('Heading 3'))}${sep}
        ${selBtn('bold', '<i class="fas fa-bold"></i>', t('Bold'))}${selBtn('italic', '<i class="fas fa-italic"></i>', t('Italic'))}${selBtn('underline', '<i class="fas fa-underline"></i>', t('Underline'))}${selBtn('strikeThrough', '<i class="fas fa-strikethrough"></i>', t('Strikethrough'))}${sep}
        ${selBtn('pc', `<i class="fas fa-font ${on('pc')}"></i><span class="absolute bottom-1.5 left-2 right-2 h-0.5" style="background:${esc(selCur.fc || 'transparent')}"></span>`, t('Text colour'))}${selBtn('ph', `<i class="fas fa-highlighter ${on('ph')}"></i><span class="absolute bottom-1.5 left-2 right-2 h-0.5" style="background:${esc(selCur.bg || 'transparent')}"></span>`, t('Highlight'))}
        ${selBtn('link', '<i class="fas fa-link"></i>', t('Link'))}${selBtn('clear', '<i class="fas fa-text-slash"></i>', t('Clear formatting'))}${sep}${selBtn('cmt', '<i class="fas fa-comment-medical"></i>', t('Comment (Ctrl+Alt+M)'))}</div>
        ${selPanel === 'pc' ? `<div class="flex flex-wrap items-center gap-1.5 pt-2 mt-1.5 border-t border-gray-800 ${selTouch ? 'max-w-[19rem]' : ''}">${none('fc0', t('Normal colour'))}${SEL_COLORS.map(c => selSwatch('fc', c, t('Text colour'))).join('')}${custom('fc', selCur.fc || EDTB.tc)}</div>` : ''}
        ${selPanel === 'ph' ? `<div class="flex flex-wrap items-center gap-1.5 pt-2 mt-1.5 border-t border-gray-800 ${selTouch ? 'max-w-[19rem]' : ''}">${none('bg0', t('No highlight'))}${SEL_HL.map(c => selSwatch('bg', c, t('Highlight'))).join('')}${custom('bg', selCur.bg || EDTB.hl)}</div>` : ''}`
}
/** First element really selected in a range of the page (a selection can start "at the end" of the previous node) */
export function selStartEl(r: Range): HTMLElement | null {
  const body = edBody(), root = r.commonAncestorContainer.nodeType === 3 ? r.commonAncestorContainer.parentNode as Node : r.commonAncestorContainer
  if (!r.collapsed) {
    const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
    for (let n: Node | null = w.currentNode.nodeType === 3 ? w.currentNode : w.nextNode(); n; n = w.nextNode()) {
      if (!(n.textContent as string).trim() || !r.intersectsNode(n) || (n === r.startContainer && r.startOffset >= (n.textContent as string).length)) continue
      return n.parentElement
    }
  }
  const el = elOf(r.startContainer) as HTMLElement
  return el && body.contains(el) ? el : null
}
/** The real colours of the selection, under the toolbar's "A" and highlighter */
export function readSelColors(r: Range | null): void {
  const body = edBody(), el = r && selStartEl(r)
  if (!el) return
  let bg = ''
  for (let e: HTMLElement | null = el; e && e !== body; e = e.parentElement) { const b = rgbHex(getComputedStyle(e).backgroundColor); if (b) { bg = b; break } }
  selCur = { fc: rgbHex(getComputedStyle(el).color), bg }
  const EDTB = useToolbarStore().tb
  EDTB.tcBar = selCur.fc || 'transparent'
  EDTB.hlBar = selCur.bg || 'transparent'
}
/** Toolbar "A" and highlighter: with selected text they open the selection menu's swatches; with no selection they apply
 *  the colour picked with the arrow (it applies to the text typed next) */
export function colorBtn(k: 'pc' | 'ph'): void {
  const r = pageRange(true) || (savedRange && !savedRange.collapsed ? savedRange : null)
  if (!r || useEditorStore().mode !== 'visual') { const EDTB = useToolbarStore().tb; return ex(k === 'pc' ? 'foreColor' : 'hiliteColor', EDTB[k === 'pc' ? 'tc' : 'hl']) }
  selRange = r.cloneRange(); readSelColors(selRange); selPanel = k
  renderSelMenu(); byId('selMenu').classList.remove('hidden'); placeSelMenu()
}
/** Computer: above the selection · phone: below (the phone's own Copy / Paste menu is above). Also on scrolling the page
 *  (ScriptEditor.vue) and resizing the window */
export function placeSelMenu(): void {
  const m = byId('selMenu')
  if (!selRange || m.classList.contains('hidden')) return
  const rc = selRange.getBoundingClientRect(), w = m.offsetWidth, h = m.offsetHeight
  let top = selTouch ? rc.bottom + 34 : rc.top - h - 8
  if (!selTouch && top < 64) top = rc.bottom + 8
  if (top + h > innerHeight - 8) top = Math.max(8, rc.top - h - (selTouch ? 56 : 8))
  m.style.top = top + 'px'
  m.style.left = Math.max(8, Math.min(rc.left + rc.width / 2 - w / 2, innerWidth - w - 8)) + 'px'
}
export function hideSelMenu(): void { byId('selMenu').classList.add('hidden'); selPanel = ''; selPicking = false }
/** The selection moved: the menu appears (or follows, or goes) a moment later */
export function selCheck(): void {
  clearTimeout(selTimer)
  selTimer = setTimeout(() => {
    const s = getSelection() as Selection, m = byId('selMenu')
    const ok = useDialogsStore().isOpen('editor') && useEditorStore().mode === 'visual' && s.rangeCount && !s.isCollapsed
      && edBody().contains(s.getRangeAt(0).commonAncestorContainer) && s.toString().trim()
    if (!ok) { if (!selPicking) hideSelMenu(); return }
    selRange = s.getRangeAt(0).cloneRange(); readSelColors(selRange)
    if (m.classList.contains('hidden')) selPanel = ''
    if (!selPicking) renderSelMenu()
    m.classList.remove('hidden')   // redrawn: the colours under "A" and the highlighter follow the selection
    placeSelMenu()
  }, selTouch ? 250 : 150)
}
/** A button of the menu (val: the swatch's colour) */
export function selDo(act: string, val?: string): void {
  if (act === 'pc' || act === 'ph') { selPanel = selPanel === act ? '' : act; renderSelMenu(); return placeSelMenu() }
  if (selRange) setSavedRange(selRange.cloneRange())
  const EDTB = useToolbarStore().tb
  if (['p', 'h1', 'h2', 'h3'].includes(act)) applyBlock(act)
  else if (act === 'fc') { EDTB.tc = val as string; applyColor('foreColor', val as string, 'tcBar') }
  else if (act === 'bg') { EDTB.hl = val as string; applyColor('hiliteColor', val as string, 'hlBar') }
  else if (act === 'fc0') clearTextColor()
  else if (act === 'bg0') ex('hiliteColor', 'transparent')
  else if (act === 'link') { hideSelMenu(); void insertLink(); return }
  else if (act === 'cmt') { hideSelMenu(); useCommentsStore().start(); return }
  else if (act === 'clear') clearFormat()
  else ex(act)
  selPicking = false
  // The selection stays: the menu stays open for the next command (e.g. bold + colour)
  const pr = pageRange(true); if (pr) selRange = pr.cloneRange()
  readSelColors(selRange); renderSelMenu()
  placeSelMenu()
}
/** The menu is on screen */
export const selMenuShown = () => !byId('selMenu').classList.contains('hidden')

/** The kept selection and the toolbar follow the selection in the page (document, first selectionchange listener) */
function onSelection(): void {
  const s = getSelection() as Selection
  if (!s.rangeCount) return
  const r = s.getRangeAt(0)
  if (!edBody().contains(r.commonAncestorContainer)) return
  setSavedRange(r.cloneRange()); readSelColors(r)   // real text colours under the toolbar "A" and highlighter
  const el = elOf(r.startContainer) as Element
  if (el && el.nodeType === 1) useToolbarStore().tb.fsize = Math.round(parseFloat(getComputedStyle(el).fontSize))
}
/** The menu's own listeners (they replace the inline handlers of its buttons, with the same effects):
 *  - pointerdown on a button: preventDefault (the selection stays) and its command; on the colour picker's label: the
 *    picker is open (selPicking: the menu is not redrawn while the browser's colour window is open)
 *  - mousedown on a button: preventDefault (for browsers that send it even so)
 *  - change of the colour picker: its colour */
function installMenuButtons(m: HTMLElement): void {
  const inMenu = <T extends Element>(e: Event, q: string): T | null => { const el = (e.target as Element).closest<T>(q); return el && m.contains(el) ? el : null }
  m.addEventListener('pointerdown', e => {
    const b = inMenu<HTMLElement>(e, 'button[data-act]')
    if (b) { e.preventDefault(); selDo(b.dataset.act as string, b.dataset.arg); return }
    if (inMenu(e, 'label[data-picker]')) selPicking = true
  })
  m.addEventListener('mousedown', e => { if (inMenu(e, 'button')) e.preventDefault() })
  m.addEventListener('change', e => { const i = inMenu<HTMLInputElement>(e, 'input[data-act]'); if (i) selDo(i.dataset.act as string, i.value) })
}
/** The listeners the legacy file added at load: selectionchange (kept selection, then the menu), resize (the menu
 *  follows; scrolling the page moves it too: ScriptEditor.vue); then the menu's own listeners */
export function installSelMenu(doc: Pick<Document, 'addEventListener'>, win: Pick<Window, 'addEventListener'>): void {
  selTouch = matchMedia('(pointer: coarse)').matches
  installMenuButtons(byId('selMenu'))
  doc.addEventListener('selectionchange', onSelection)
  doc.addEventListener('selectionchange', selCheck)
  win.addEventListener('resize', placeSelMenu)
}
